import { HttpClient } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ModalConfiguration } from '../../../dynamic/models/modal.model';
import { DynamicLoaderService } from '../../../dynamic/services/dynamic-loader.service';
import { HelpContentDetails } from '../../../models/help/help-content-details.model';
import { Identifier } from '../../../models/storage.models';
import { BaseHttpService } from '../../../services/base-http.service';
import { ClinicHttpService } from '../../../services/clinic-http.service';
import { LoggerService } from '../../../services/logger.service';
import { HelpIframeModalComponent } from './help-iframe/help-iframe-modal.component';

@Injectable({
  providedIn: 'root',
})
export class HelpService extends BaseHttpService implements OnDestroy {
  private readonly logger = inject(LoggerService);
  private destroyed = new Subject<void>();
  private subscription: Subscription = new Subscription();
  private baseUrl = this.coApiUrl;

  constructor(
    private dynamicLoaderService: DynamicLoaderService,
    private clinicHttpService: ClinicHttpService,
    private http: HttpClient
  ) {
    super();
  }

  ngOnDestroy(): void {
    this.destroyed.next();
  }

  getAutoPopoverHelpIds(): Observable<{ helpContentIds: number[] }> {
    return this.http.get<{ helpContentIds: number[] }>(`${this.baseUrl}helpcontents/myrecords`);
  }

  getHelpContentDetails(helpContentId: number): Observable<HelpContentDetails> {
    return this.http.get<HelpContentDetails>(`${this.baseUrl}helpcontents/${helpContentId}/details`);
  }

  openHelpInNewWindow(helpContentId: number): void {
    const data = { helpContentId: helpContentId, iframeUrl: '' };

    this.goToAddatechHelpSystem(data);
  }

  openIframeHelpModal(helpContentId: number) {
    const configuration: ModalConfiguration = {
      panelClass: 'popup medium',
      animation: 'scale',
      outsideClick: false,
    };

    const data = { helpContentId: helpContentId, iframeUrl: '' };

    this.goToAddatechHelpSystem(data, configuration);
  }

  goToHelpSystem(data: any, configuration?: ModalConfiguration): void {
    this.subscription.add(
      this.clinicHttpService.getNonceIdForHelpSystemSession().subscribe(
        (nonceId) => {
          if (nonceId) {
            const clinicGroupId = localStorage.getItem(Identifier.TYPE_CLINIC_GROUP_ID);
            const lang = localStorage.getItem(Identifier.TYPE_LANGUAGE) || 'en-CA';
            const franchiseId = localStorage.getItem(Identifier.TYPE_FRANCHISE_ID);
            const clinicId = localStorage.getItem(Identifier.TYPE_CLINIC_ID);
            const addatechHelpSystemUrl = 'https://help-addatechqa.clinicmaster.com/';
            const helpSystemSessionUrl = `${addatechHelpSystemUrl}help/content/${data.helpContentId}?nonce=${nonceId}&franchiseId=${franchiseId}&clinicGroupId=${clinicGroupId}&clinicId=${clinicId}&iframe=1&lang=${lang}`;

            if (!configuration) {
              window.open(helpSystemSessionUrl, '_blank');
              return;
            }

            data.iframeUrl = helpSystemSessionUrl;
            this.dynamicLoaderService
              .open<HelpIframeModalComponent>(HelpIframeModalComponent, configuration, data)
              .then((container) => {
                if (!container.componentRef) {
                  return;
                }

                container.componentRef.instance.closeClick
                  .pipe(takeUntil(container.componentRef.instance.destroyed))
                  .subscribe(() => container.close());
              });
          }
        },
        (error) => {
          this.logger.error('NonceId for Help System session error:', error);
        }
      )
    );
  }

  goToAddatechHelpSystem(data: any, configuration?: ModalConfiguration): void {
    this.subscription.add(
      this.clinicHttpService.getNonceIdForAddatechHelpSystemSession().subscribe(
        (addaNonce) => {
          if (addaNonce) {
            const lang = localStorage.getItem(Identifier.TYPE_LANGUAGE) || 'en-CA';
            const addatechHelpSystemUrl = 'https://help-addatechqa.clinicmaster.com/';
            const helpSystemSessionUrl = `${addatechHelpSystemUrl}help/content/${data.helpContentId}?nonce=${addaNonce.id}&franchiseId=${addaNonce.clinicGroupFranchiseId}&clinicGroupId=${addaNonce.clinicGroupId}&clinicId=${addaNonce.clinicId}&iframe=1&lang=${lang}`;

            if (!configuration) {
              window.open(helpSystemSessionUrl, '_blank');
              return;
            }

            data.iframeUrl = helpSystemSessionUrl;
            this.dynamicLoaderService
              .open<HelpIframeModalComponent>(HelpIframeModalComponent, configuration, data)
              .then((container) => {
                if (!container.componentRef) {
                  return;
                }

                container.componentRef.instance.closeClick
                  .pipe(takeUntil(container.componentRef.instance.destroyed))
                  .subscribe(() => container.close());
              });
          }
        },
        (error) => {
          this.logger.error('NonceId for Help System session error:', error);
        }
      )
    );
  }
}
